import { SURFACE_3D_COLOR_TOKENS } from './surface3dColorTokens';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ViewIds } from '@ardoq/api-types';
import { registerViewPane } from 'tabview/assistantShared/registerViewPane';
import { colors } from '@ardoq/design-tokens';
import { FlexBox } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import { t } from '@ardoq/i18n';
import { formatNumber } from '@ardoq/locale';
import {
  POINTS_LOOK_CONCEPTS,
  Points3DChart,
  type PointsLook,
  resolveCssColor,
  type Points3DChartProps,
  type Points3DViewSettings,
} from '@ardoq/xyz-charts';
import styled from 'styled-components';
import { VisualizationContainer } from 'tabview/ViewContainer';
import type { Points3DSourceData } from './sourceData/buildPoints3DSourceData';
import type { Points3DViewProperties } from './types';
import { ChartEmptyState } from 'tabview/chartShared/ChartEmptyState';
import { ViewLegend } from '@ardoq/view-legend';
import { ViewLegendContainer } from '@ardoq/graph';
import { noop } from 'lodash';
import type { getViewLegendLabels } from 'viewLegend/getViewLegendLabels';
import type { Points3DLegendRows } from './points3dLegend';

/** See `Surface3DView` for why `min-height: 0` is what keeps the canvas from collapsing. */
const Points3DLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const ChartArea = styled(VisualizationContainer)`
  overflow: hidden;
  min-height: 0;
`;

/* The settings arrive beside the derived data rather than inside it - see `ContourViewProps`. */
export type Points3DViewProps = Points3DSourceData & {
  viewSettings: Points3DViewSettings;
  legendRows: Points3DLegendRows;
  viewLegendLabels: ReturnType<typeof getViewLegendLabels>;
  /**
   * Which of the two charts this is.
   *
   * Not a setting: scatter is the one without size variation and bubble is the one with it, each
   * behind its own flag. A dropdown that turned one into the other would make two registrations
   * pointless.
   */
  viewId: ViewIds.SCATTER_3D | ViewIds.BUBBLE_3D;
  showSizeVariation: boolean;
};

/** The projected look, with the wall projections only while the reader wants them. */
export const toPoints3DLook = (showWallProjections: boolean): PointsLook =>
  showWallProjections
    ? POINTS_LOOK_CONCEPTS.projected
    : {
        ...POINTS_LOOK_CONCEPTS.projected,
        wallProjection: {
          ...POINTS_LOOK_CONCEPTS.projected.wallProjection,
          opacity: 0,
        },
      };

const useChartColors = (element: HTMLElement | null) =>
  useMemo(
    () => ({
      surfaceColor: resolveCssColor(element, colors.surfaceDefault, '#ffffff'),
      inkColor: resolveCssColor(element, colors.textDefault, '#1F2126'),
      gridColor: resolveCssColor(element, colors.borderSubtle, '#DADADA'),
      /* The axis box's faces, which these charts draw now. The same token the surface chart
         resolves, so the two read as one chrome rather than two: `surfaceDefaultSubtle` is
         `grey50` on a light surface and `grey900` on a dark one, which is what makes "one step off
         the surface" the theme's decision rather than this view's. */
      panelColor: resolveCssColor(
        element,
        SURFACE_3D_COLOR_TOKENS.panel,
        '#FAFAFA'
      ),
    }),
    [element]
  );

/**
 * Scatter and bubble in one view, as they are one chart in the package.
 *
 * The only differences that reach the user are whether the radius field varies mark area and
 * which name the view goes by; everything else - the source data, the axes, the empty states,
 * the exclusion accounting - is the same, and writing it twice would mean fixing it twice.
 */
export const Points3DView = ({
  points,
  axes,
  viewSettings,
  xAxisTitle,
  yAxisTitle,
  zAxisTitle,
  radiusAxisTitle,
  isViewEmpty,
  emptyReason,
  excludedCount,
  numericFields,
  viewInstanceId,
  locale,
  i18nLocale,
  viewId,
  showSizeVariation,
  legendRows,
  viewLegendLabels,
}: Points3DViewProps) => {
  /* State behind a callback ref, not a `useRef`, and that is the difference between the colors
     resolving and not. A ref is populated after commit, so reading `.current` during render hands
     `useChartColors` a null on the render that mounts the layout - and `resolveCssColor` then
     returns its hardcoded light fallbacks. Nothing re-renders this view when the ref attaches, so a
     dark theme kept a white panel with near-black ink on it until some unrelated stream emission
     happened along. `setContainer` is a state update, so the colors are resolved against the live
     element on the very next render. Same fix as `Surface3DView`. */
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const chartColors = useChartColors(container);

  /* The chart publishes its export hook once it has a canvas. Held in a ref and read through a
     stable closure so the registration below does not have to re-run when it arrives. */
  const createExportCanvasRef = useRef<(() => OffscreenCanvas | null) | null>(
    null
  );
  const onExportCanvasAvailable = useCallback(
    (createExportCanvas: (() => OffscreenCanvas | null) | null) => {
      createExportCanvasRef.current = createExportCanvas;
    },
    []
  );

  /* `registerViewPane` rather than `updateViewProperties` alone, for the two things that come with
     it. The export hook, so the PNG and slide paths read the canvas instead of cloning a DOM that
     carries none of its pixels. And the view *context*: `viewModifiers$` holds one for the whole
     studio and never clears it on a view change, so a view that publishes properties without one
     inherits the last view's - `viewActionsOptions` falls to its default and renders Zoom in /
     Zoom out / Optimize layout built from a stale context, wired to another view's handlers or to
     `DEFAULT_VIEW_CONTEXT`'s noops. Registering leaves all three null, which is what hides them. */
  useEffect(() => {
    registerViewPane({
      viewId,
      viewProperties: {
        viewSettings,
        numericFields,
        i18nLocale,
        isEmptyView: isViewEmpty,
        viewInstanceId,
        showSizeVariation,
      } satisfies Points3DViewProperties,
      isLegendActive: viewSettings.isLegendActive,
      getContainer: () => container,
      createExportCanvas: () => createExportCanvasRef.current?.() ?? null,
      viewInstanceId,
    });
  }, [
    viewId,
    viewSettings,
    numericFields,
    i18nLocale,
    isViewEmpty,
    viewInstanceId,
    showSizeVariation,
    container,
  ]);

  const format = useMemo(
    () => (value: number) => formatNumber(value, 'default', locale),
    [locale]
  );

  const style = useMemo(() => ({ showSizeVariation }), [showSizeVariation]);
  const look = useMemo(
    () => toPoints3DLook(viewSettings.showWallProjections),
    [viewSettings.showWallProjections]
  );

  /* Rendered by both branches rather than only the chart one, and that is the whole point. Most
     screen readers announce a change inside a region that was already in the accessibility tree, so
     a region arriving together with its first message announces nothing. Nothing is preselected
     here, so every user reaches the chart by picking the last field - which is the moment
     `isViewEmpty` flips and, if any component lacks a value, the moment the first message exists.
     Returned from the empty branch as well, under the same `Points3DLayout` at the same position,
     so React reconciles the two as one element and the region survives the switch.

     Padding stays conditional, or an empty region would hold a strip above the chart all session. */
  const exclusionCopy = t(
    i18nLocale,
    'views:points3d.exclusionNotice',
    {
      plotted: points.length,
      total: points.length + excludedCount,
    },
    '{{plotted}} of {{total}} components plotted. The rest have no value for one of the chosen fields.'
  );

  /* The notice the exported file has to carry, since that file is the canvas and nothing else -
     without it the PNG claims to plot everything. Shown only when it is true of the picture, the
     same as the region below. */
  const exportCaptions = useMemo(
    () => (excludedCount > 0 && !isViewEmpty ? [exclusionCopy] : []),
    [excludedCount, isViewEmpty, exclusionCopy]
  );

  const exclusionNotice = (
    <FlexBox
      role="status"
      aria-live="polite"
      padding={excludedCount > 0 ? 'small' : 'none'}
      paddingBottom="none"
    >
      {excludedCount > 0 && !isViewEmpty && (
        <Text color="textSubtle" variant="caption">
          {exclusionCopy}
        </Text>
      )}
    </FlexBox>
  );

  /* One clause per channel. A `Record` over every `ChannelKind` rather than a formatter taking a
     bag of field names: adding a channel to the package then stops the build here until it has
     words, instead of compiling into a chart that announces it nowhere. */
  const channelLabels = useMemo<Points3DChartProps['channelLabels']>(
    () => ({
      across: field =>
        t(
          i18nLocale,
          'views:points3d.channel.across',
          { field },
          '{{field}} across'
        ),
      deep: field =>
        t(
          i18nLocale,
          'views:points3d.channel.deep',
          { field },
          '{{field}} deep'
        ),
      height: field =>
        t(
          i18nLocale,
          'views:points3d.channel.height',
          { field },
          '{{field}} as height'
        ),
      size: field =>
        t(
          i18nLocale,
          'views:points3d.channel.size',
          { field },
          '{{field}} as mark size'
        ),
      color: field =>
        t(
          i18nLocale,
          'views:points3d.channel.color',
          { field },
          '{{field}} as color'
        ),
    }),
    [i18nLocale]
  );

  /* The frame around them, built here because the chart package has no locale. Typed from the
     prop rather than restated, so the shape cannot drift. */
  const formatAccessibleName = useMemo<
    Points3DChartProps['formatAccessibleName']
  >(
    () =>
      ({ pointCount, isBubble, channels }) =>
        isBubble
          ? t(
              i18nLocale,
              'views:points3d.accessibleName.bubble',
              { count: pointCount, channels: channels.join(', ') },
              '3D bubble chart, {{count}} components. {{channels}}. Use the arrow keys to rotate and plus or minus to zoom.'
            )
          : t(
              i18nLocale,
              'views:points3d.accessibleName.scatter',
              { count: pointCount, channels: channels.join(', ') },
              '3D scatter chart, {{count}} components. {{channels}}. Use the arrow keys to rotate and plus or minus to zoom.'
            ),
    [i18nLocale]
  );

  if (isViewEmpty) {
    return (
      <Points3DLayout ref={setContainer}>
        {exclusionNotice}
        <ChartEmptyState
          emptyReason={emptyReason}
          noFieldsSelected={{
            title: t(
              i18nLocale,
              'views:points3d.emptyState.noFieldsSelected.title',
              'Choose the fields to plot'
            ),
            message: showSizeVariation
              ? t(
                  i18nLocale,
                  'views:points3d.emptyState.noFieldsSelected.messageBubble',
                  'This view reads three numeric fields to place each mark - one across, one deep, one for height - and a fourth for mark size. Pick a field for each in the settings bar above.'
                )
              : t(
                  i18nLocale,
                  'views:points3d.emptyState.noFieldsSelected.message',
                  'This view reads three numeric fields: one across, one deep, and one for height. Pick a field for each in the settings bar above.'
                ),
          }}
          noComponents={{
            title: t(
              i18nLocale,
              'views:points3d.emptyState.noComponentsInView.title',
              'There is nothing in view to plot'
            ),
            message: t(
              i18nLocale,
              'views:points3d.emptyState.noComponentsInView.message',
              'No components are in view, so there is nothing to place.'
            ),
          }}
          /* Reached here only when every component was excluded, since one mark is a chart. That
           is a different problem from the gridded charts' version of this reason, and it has a
           different remedy. */
          tooFewPositions={{
            title: t(
              i18nLocale,
              'views:points3d.emptyState.nothingToPlot.title',
              'No components have a value for all three fields'
            ),
            explanation: t(
              i18nLocale,
              'views:points3d.emptyState.nothingToPlot.message1',
              'Every component was left out for having no value on the X, Y, or height field, so there is nothing to place.'
            ),
            remedy: t(
              i18nLocale,
              'views:points3d.emptyState.nothingToPlot.message2',
              'Choose fields that more of these components have values for.'
            ),
          }}
        />
      </Points3DLayout>
    );
  }

  return (
    <Points3DLayout ref={setContainer}>
      {exclusionNotice}
      <ChartArea>
        <ViewLegendContainer visible={viewSettings.isLegendActive}>
          <ViewLegend
            componentTypes={[]}
            referenceTypes={[]}
            additionalRows={legendRows}
            activeDiffMode={null}
            itemClickTrackingFunction={noop}
            labels={viewLegendLabels}
          />
        </ViewLegendContainer>
        <Points3DChart
          points={points}
          axes={axes}
          xTitle={xAxisTitle}
          yTitle={yAxisTitle}
          zTitle={zAxisTitle}
          /* Undefined on the scatter rather than the label, so `describeChart`'s guard keeps
             dropping the channel on a chart that has no size encoding. On the bubble it is the one
             channel that distinguishes the two views, and the legend does not name it. */
          radiusTitle={showSizeVariation ? radiusAxisTitle : undefined}
          formatAccessibleName={formatAccessibleName}
          channelLabels={channelLabels}
          markListLabel={t(
            i18nLocale,
            'views:points3d.markList.label',
            'Plotted components'
          )}
          onExportCanvasAvailable={onExportCanvasAvailable}
          exportCaptions={exportCaptions}
          ramp={viewSettings.ramp}
          bands={viewSettings.bands}
          style={style}
          look={look}
          format={format}
          {...chartColors}
        />
      </ChartArea>
    </Points3DLayout>
  );
};
